import { useState, type ReactNode } from "react";
import { useNavigate } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { Copy, LogIn, LogOut, KeyRound, Pencil, Trash2 } from "lucide-react";
import {
  ContextMenu,
  ContextMenuTrigger,
  ContextMenuContent,
  ContextMenuItem,
  ContextMenuSeparator,
} from "../ui/context-menu";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "../ui/dialog";
import { Button } from "../ui/button";
import { copyRoomCode, getRoomCode, useDeleteRoom, useLeaveRoom, type Room } from "../../lib/rooms";
import { RoomDialog } from "./RoomDialog";
import {
  roomDialogClass,
  roomDialogDescriptionClass,
  roomDialogTitleClass,
  roomErrorMessage,
  roomFormClass,
  roomFormErrorClass,
} from "./room-form";

export function RoomMenu({ room, children }: { room: Room; children: ReactNode }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [dialog, setDialog] = useState<"" | "edit" | "delete">("");
  const [notice, setNotice] = useState("");
  const remove = useDeleteRoom(room.id);
  const leaveRoom = useLeaveRoom(room.id);
  const open = (type: "edit" | "delete") => {
    remove.reset();
    setDialog(type);
  };
  async function copy() {
    try {
      await navigator.clipboard.writeText(new URL(`/room/${room.id}`, window.location.origin).href);
      setNotice(t("home.copied"));
    } catch {
      setNotice(t("home.copyFailed"));
    }
  }
  async function copyCode() {
    try {
      await copyRoomCode(getRoomCode(room.id));
      setNotice(t("home.codeCopied"));
    } catch (cause) {
      setNotice(
        t(`home.errors.${(cause as Error).message}`, {
          defaultValue: t("home.codeCopyFailed"),
        }),
      );
    }
  }
  function leave() {
    leaveRoom.mutate(undefined, {
      onError: (cause) => setNotice(roomErrorMessage(t, cause.message)),
    });
  }
  function items(Item: typeof ContextMenuItem, Separator: typeof ContextMenuSeparator) {
    return (
      <>
        <Item onSelect={() => void navigate({ to: "/room/$roomId", params: { roomId: room.id } })}>
          <LogIn />
          {t("home.join")}
        </Item>
        <Item onSelect={copy}>
          <Copy />
          {t("home.copyUrl")}
        </Item>
        {room.canCopyCode ? (
          <Item onSelect={copyCode}>
            <KeyRound />
            {t("home.copyCode")}
          </Item>
        ) : null}
        {room.visited && !room.canManage ? (
          <Item onSelect={leave}>
            <LogOut />
            {t("home.leaveRecent")}
          </Item>
        ) : null}
        {room.canManage ? (
          <>
            <Separator />
            <Item onSelect={() => open("edit")}>
              <Pencil />
              {t("home.edit")}
            </Item>
            <Item variant="destructive" onSelect={() => open("delete")}>
              <Trash2 />
              {t("home.delete")}
            </Item>
          </>
        ) : null}
      </>
    );
  }
  function submit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!remove.isPending) remove.mutate(undefined, { onSuccess: () => setDialog("") });
  }
  return (
    <>
      <ContextMenu>
        <ContextMenuTrigger asChild>
          <div className="relative min-w-0">
            {children}
            {notice ? (
              <span className="relative z-2 mt-2 block text-xs text-muted-foreground" role="status">
                {notice}
              </span>
            ) : null}
          </div>
        </ContextMenuTrigger>
        <ContextMenuContent>{items(ContextMenuItem, ContextMenuSeparator)}</ContextMenuContent>
      </ContextMenu>
      <RoomDialog
        room={room}
        open={dialog === "edit"}
        onOpenChange={(value) => {
          if (!value) setDialog("");
        }}
      />
      <Dialog
        open={dialog === "delete"}
        onOpenChange={(value) => {
          if (!remove.isPending && !value) setDialog("");
        }}
      >
        <DialogContent className={roomDialogClass}>
          <DialogHeader>
            <DialogTitle className={roomDialogTitleClass}>{t("home.deleteTitle")}</DialogTitle>
            <DialogDescription className={roomDialogDescriptionClass}>
              {t("home.deleteBody", { name: room.name })}
            </DialogDescription>
          </DialogHeader>
          <form noValidate className={roomFormClass} onSubmit={submit}>
            {remove.error ? (
              <p className={roomFormErrorClass} role="alert">
                {roomErrorMessage(t, remove.error.message)}
              </p>
            ) : null}
            <DialogFooter>
              <Button
                type="button"
                variant="ghost"
                disabled={remove.isPending}
                onClick={() => setDialog("")}
              >
                {t("common.cancel")}
              </Button>
              <Button type="submit" variant="destructive" disabled={remove.isPending}>
                {t(remove.isPending ? "home.deleting" : "home.delete")}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </>
  );
}
